
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { TreatmentServiceCard } from "@/components/TreatmentServiceCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PageTitle } from "@/components/PageTitle";

const Orthodontics = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQs specific to Orthodontics
  const faqs = [
    {
      question: "At what age should orthodontic treatment begin?",
      answer: "While orthodontic treatment can begin at any age, the American Association of Orthodontists recommends a first orthodontic evaluation by age 7. Early intervention can guide jaw growth and incoming permanent teeth. However, orthodontic treatment is effective for patients of all ages, including adults."
    },
    {
      question: "How long does orthodontic treatment typically last?",
      answer: "The duration of orthodontic treatment varies depending on individual needs, but typically ranges from 12 to 24 months. Minor corrections may take less time, while more complex cases might require longer treatment. We'll provide a personalized timeline during your consultation."
    },
    {
      question: "Is Invisalign as effective as traditional braces?",
      answer: "Invisalign can be highly effective for many orthodontic issues. It works well for mild to moderate cases of crowding, spacing, and certain bite problems. However, for some complex orthodontic issues, traditional braces may be more appropriate. We'll recommend the most effective option based on your specific needs."
    },
    {
      question: "Will orthodontic treatment be painful?",
      answer: "Patients typically experience some discomfort for a few days after adjustments or when switching to a new Invisalign aligner. This is normal and indicates the teeth are moving. The discomfort is usually mild and can be managed with over-the-counter pain relievers if needed."
    },
    {
      question: "How do I care for my teeth during orthodontic treatment?",
      answer: "Maintaining excellent oral hygiene is crucial during orthodontic treatment. This includes brushing after every meal, flossing daily (using floss threaders or water flossers with braces), and keeping regular dental cleaning appointments. For Invisalign users, it's important to clean your aligners regularly and remove them when eating or drinking anything other than water."
    }
  ];

  // Testimonials for Orthodontics
  const testimonials = [
    {
      name: "Rahul Kapoor",
      comment: "I was hesitant about getting braces as an adult, but Dr. Asrani assured me that Invisalign would be discreet and effective. The results have been amazing, and most people didn't even notice I was wearing aligners during treatment. My confidence has soared with my new smile!",
      rating: 5
    },
    {
      name: "Nisha Patel",
      comment: "My son had significant crowding issues, and we were worried about traditional braces. The team at Asrani Dental made the entire process so easy and comfortable. They were patient with all our questions and the results are fantastic. His smile is now perfectly aligned!",
      rating: 5
    },
    {
      name: "Karan Malhotra",
      comment: "After years of being self-conscious about my crooked teeth, I finally decided to get treatment at Asrani Dental. Their orthodontic approach was personalized to my specific needs. The team was supportive throughout my journey, and I couldn't be happier with my straight, beautiful smile.",
      rating: 5
    }
  ];

  const handleBookAppointment = () => {
    window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank");
  };

  return (
    <div className="min-h-screen">
      <PageTitle title="Braces and Aligners in Nagpur | Cost of Braces in Nagpur" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Orthodontics
            </h1>
            <p className="text-lg text-dental-muted">
              Achieve a straighter, healthier smile with our modern orthodontic treatments tailored for patients of all ages.
            </p>
            <div className="mt-8">
              <Button className="btn-primary" onClick={handleBookAppointment}>
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/lovable-uploads/9fc58907-ed48-4130-8519-586ca6dd4de7.png" 
                alt="Young woman smiling with braces - Orthodontics at Asrani Dental Clinic" 
                className="rounded-xl shadow-soft w-full h-auto"
              />
            </div>
            
            <div>
              <SectionTitle 
                title="Modern Orthodontic Solutions" 
                subtitle="Orthodontics"
                description="Our orthodontic services use the latest technology to straighten teeth, correct bite issues, and improve both the function and appearance of your smile."
                alignment="left"
              />
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Customized Treatment</h3>
                    <p className="text-dental-muted">Personalized orthodontic plans designed specifically for your unique dental structure and goals.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Advanced Technology</h3>
                    <p className="text-dental-muted">Digital imaging and planning tools that ensure precise, efficient treatment outcomes.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Discreet Options</h3>
                    <p className="text-dental-muted">Clear and nearly invisible treatment alternatives for patients concerned about aesthetics.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">All Ages Welcome</h3>
                    <p className="text-dental-muted">Orthodontic solutions for children, teenagers, and adults at any stage of life.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Treatments Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle 
            title="Our Orthodontic Treatments" 
            subtitle="Services We Offer"
            description="We provide a range of orthodontic options to help you achieve a properly aligned, beautiful smile."
            alignment="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <TreatmentServiceCard 
              title="Invisalign Clear Aligners"
              description="Nearly invisible removable aligners that gradually straighten teeth without the look and restrictions of traditional braces."
              icon={<img src="/lovable-uploads/e618cd51-dda8-4b0b-a059-6853758acfb7.png" alt="Invisalign" className="w-12 h-12" />}
            />
            
            <TreatmentServiceCard 
              title="Traditional Braces"
              description="Metal or ceramic brackets and wires that effectively correct a wide range of orthodontic issues, especially for complex cases."
              icon={<img src="/lovable-uploads/5a078636-e2db-4c4b-9ff4-103181291464.png" alt="Traditional Braces" className="w-12 h-12" />}
            />
            
            <TreatmentServiceCard 
              title="Early Intervention"
              description="Orthodontic treatment for young children to guide proper jaw development and create space for incoming permanent teeth."
              icon={<img src="/lovable-uploads/b3a1cdfe-4120-4e6b-89ff-581eada505cd.png" alt="Early Intervention" className="w-12 h-12" />}
            />
            
            <TreatmentServiceCard 
              title="Retainers"
              description="Custom-made appliances designed to maintain teeth in their corrected positions after active orthodontic treatment."
              icon={<img src="/lovable-uploads/2b06da5e-1d8a-4e3c-8235-4f64bf4ed4b4.png" alt="Retainers" className="w-12 h-12" />}
            />
            
            <TreatmentServiceCard 
              title="Accelerated Orthodontics"
              description="Techniques to speed up tooth movement, reducing overall treatment time without compromising results."
              icon={<img src="/lovable-uploads/d4316e9e-89df-4398-9925-c659dba24fad.png" alt="Accelerated Orthodontics" className="w-12 h-12" />}
            />
            
            <TreatmentServiceCard 
              title="Bite Correction"
              description="Specialized treatments to address overbites, underbites, crossbites, and other bite alignment issues."
              icon={<img src="/lovable-uploads/f8ec0cd0-df62-49b2-ae32-979cd979fa07.png" alt="Bite Correction" className="w-12 h-12" />}
            />
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle 
            title="Patient Testimonials" 
            subtitle="What Our Patients Say"
            description="Hear from our patients about their orthodontic treatment experiences and results."
            alignment="center"
          />
          
          <div className="max-w-4xl mx-auto mt-12">
            <ServiceTestimonials testimonials={testimonials} />
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Common Questions"
            description="Find answers to common questions about orthodontic treatments."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>
      
      {/* Next Steps Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">Ready to Start Your Orthodontic Journey?</h2>
            <p className="text-lg text-dental-muted mb-8">
              Contact our office today to schedule your orthodontic consultation. We'll help you achieve the straight, healthy smile you deserve.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button className="btn-primary flex items-center text-base group" onClick={handleBookAppointment}>
                Book Appointment
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              
              <Button className="btn-secondary flex items-center text-base" onClick={() => window.location.href = '/contact'}>
                Contact Us
              </Button>
            </div>
            
            <div className="mt-10">
              <p className="text-dental-muted mb-4">Interested in our other dental services?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services/general-dentistry-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  General Dentistry
                </Link>
                <Link to="/services/cosmetic-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Cosmetic Dentistry
                </Link>
                <Link to="/services/full-mouth-implants-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Full Mouth & Implants
                </Link>
                <Link to="/services/painless-rct-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Painless RCT
                </Link>
                <Link to="/services/pediatric-dentistry-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Pediatric Dentistry
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CTASection
        title="Achieve a Perfectly Aligned Smile"
        description="Our advanced orthodontic treatments can help you or your child achieve a straight, beautiful smile. Schedule your consultation today."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Learn More"
        primaryButtonAction={handleBookAppointment}
        secondaryButtonAction={() => window.location.href = "/about"}
      />
      
      <Footer />
    </div>
  );
};

export default Orthodontics;
